/**
 * Dünner Abonnent des Transcript-Sockets (Doc 04 §1: unabhängig von der
 * Capture-Lib nutzbar). Reconnect mit `replayFrom = lastSeq + 1`,
 * verwirft Duplikate (seq ≤ lastSeq).
 */
import type { RegieAuth, SubscribeScope, TranscriptServerMessage } from '@voice/protocol';
import { backoffDelay, FATAL_CLOSE_CODES, FATAL_ERROR_CODES } from './backoff.js';

export interface TranscriptClientOptions {
  /** Basis-URL des Gateways (ws:// bzw. wss://, ohne Pfad) */
  gatewayUrl: string;
  sessionId: string;
  joinToken?: string;
  auth?: RegieAuth;
  scope?: SubscribeScope;
  sourceIds?: string[];
  replayFrom?: number;
  WebSocketImpl?: typeof WebSocket;
  onMessage: (msg: TranscriptServerMessage) => void;
  onState?: (state: 'connecting' | 'open' | 'reconnecting' | 'closed' | 'error', detail?: string) => void;
}

export class TranscriptClient {
  lastSeq = 0;
  private ws: WebSocket | null = null;
  private closed = false;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private sourceIds: string[];

  constructor(private readonly opts: TranscriptClientOptions) {
    this.sourceIds = [...(opts.sourceIds ?? [])];
    if (opts.replayFrom !== undefined) this.lastSeq = Math.max(0, opts.replayFrom - 1);
  }

  connect(): void {
    this.closed = false;
    this.open(this.opts.replayFrom);
  }

  /** Eigene Quellen ändern (scope own) — wirkt per Neuverbindung mit Replay. */
  setSourceIds(ids: string[]): void {
    this.sourceIds = [...ids];
    if (this.ws) this.ws.close(4000, 'resubscribe');
  }

  close(): void {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close(1000, 'bye');
    this.ws = null;
  }

  private open(replayFrom?: number): void {
    const WS = this.opts.WebSocketImpl ?? WebSocket;
    const base = this.opts.gatewayUrl.replace(/\/+$/, '');
    const ws = new WS(`${base}/transcript?sessionId=${encodeURIComponent(this.opts.sessionId)}`);
    this.ws = ws;
    this.opts.onState?.(this.attempt === 0 ? 'connecting' : 'reconnecting');
    ws.addEventListener('open', () => {
      const sub: Record<string, unknown> = { v: 2, type: 'subscribe' };
      if (this.opts.joinToken) sub['joinToken'] = this.opts.joinToken;
      if (this.opts.auth) sub['auth'] = this.opts.auth;
      if (this.opts.scope) sub['scope'] = this.opts.scope;
      if (this.sourceIds.length) sub['sourceIds'] = this.sourceIds;
      const from = replayFrom ?? (this.lastSeq > 0 ? this.lastSeq + 1 : undefined);
      if (from !== undefined) sub['replayFrom'] = from;
      ws.send(JSON.stringify(sub));
    });
    ws.addEventListener('message', (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return;
      let msg: TranscriptServerMessage;
      try {
        msg = JSON.parse(ev.data) as TranscriptServerMessage;
      } catch {
        return;
      }
      if (msg.type === 'subscribed') {
        this.attempt = 0;
        this.opts.onState?.('open');
      }
      if (msg.type === 'error' && FATAL_ERROR_CODES.has(msg.code)) {
        this.closed = true;
        this.opts.onState?.('error', `${msg.code}: ${msg.message}`);
      }
      if (msg.type === 'ping') {
        ws.send(JSON.stringify({ v: 2, type: 'pong' }));
        return;
      }
      if ('seq' in msg && typeof msg.seq === 'number') {
        if (msg.seq <= this.lastSeq) return; // Duplikat aus Replay
        this.lastSeq = msg.seq;
      }
      this.opts.onMessage(msg);
    });
    ws.addEventListener('close', (ev: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closed || FATAL_CLOSE_CODES.has(ev.code)) {
        this.opts.onState?.(this.closed ? 'closed' : 'error', ev.reason);
        return;
      }
      const delay = ev.code === 4000 ? 0 : backoffDelay(this.attempt++);
      this.opts.onState?.('reconnecting');
      this.timer = setTimeout(() => this.open(), delay);
    });
  }
}
